import { c as _c } from "react/compiler-runtime";
import { locale } from "@locale";
import classNames from "classnames";
import React, { useMemo } from 'react';
import FilmwebLink from 'src/components/filmweblink/FilmwebLink';
import OptionalPosterInfo from "src/components/posterrow/OptionalPosterInfo";
import PosterImage from "src/components/posterrow/PosterImage";
import { buildFilmUrl } from "src/lib/buildFilmUrl";
import { isFilmwebIdGraphQLUserContent, type IFilmwebIdGraphQLUserContent } from "src/lib/filmwebid/useMovieIdentifier";
import { type Movie, type StreamingContent, type UserFavouritesItem, type WatchableContent } from 'src/lib/movieinfo/movieinfotypes';
import { isMovie, isStreamingContent, isWatchableContent } from "src/lib/movieinfo/typeGuards";
import type { IFilmomtale } from 'src/lib/types/Filmomtale';
import cssPosterStyles from "./CSSPoster.module.css";
import type OptionalPosterInfoType from "src/components/posterrow/OptionalPosterDisplayType";
const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND!;

//#region [Props]
export type PosterProps = {
  className?: string;
  film: IFilmomtale | Movie | StreamingContent | WatchableContent | IFilmwebIdGraphQLUserContent | UserFavouritesItem;
  sizes?: string;
  children?: React.ReactNode;
  posterMissingText?: string;
  showMovieTitleIfPosterIsMissing?: boolean;
  hideTitle?: boolean;
  width?: number;
  height?: number;
  optionalDisplay?: OptionalPosterInfoType;
  longTitle?: boolean;
  mediumTitle?: boolean;
  hideKinoklubbBanner?: boolean;
  imageLoading?: "lazy" | "eager";
  headerLevel?: 2 | 3 | 4 | 5 | 6;
  posterOverlay?: React.ReactNode;
  showInNaturalFormat?: boolean;
  posterMissingClassName?: string;
};

//#endregion

//#region [Component]
export default function Poster(t0) {
  const $ = _c(53);
  const {
    film: filmProp,
    className,
    sizes,
    posterMissingText: t1,
    showMovieTitleIfPosterIsMissing: t2,
    hideTitle: t3,
    width,
    height,
    optionalDisplay,
    longTitle: t4,
    mediumTitle: t5,
    hideKinoklubbBanner: t6,
    children,
    headerLevel: t7,
    imageLoading,
    posterOverlay,
    showInNaturalFormat,
    posterMissingClassName
  } = t0;
  const posterMissingText = t1 === undefined ? locale.poster.posterMissing : t1;
  const showMovieTitleIfPosterIsMissing = t2 === undefined ? false : t2;
  const hideTitle = t3 === undefined ? false : t3;
  const longTitle = t4 === undefined ? false : t4;
  const mediumTitle = t5 === undefined ? false : t5;
  const hideKinoklubbBanner = t6 === undefined ? false : t6;
  const headerLevel = t7 === undefined ? 2 : t7;
  let t8;
  bb0: {
    if (!filmProp) {
      let t9;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [null, false, false];
        $[0] = t9;
      } else {
        t9 = $[0];
      }
      t8 = t9;
      break bb0;
    }
    let t9;
    if ($[1] !== filmProp) {
      t9 = buildFilmUrl(filmProp);
      $[1] = filmProp;
      $[2] = t9;
    } else {
      t9 = $[2];
    }
    const url = t9;
    let hasPoster;
    let isKinoklubb = false;
    const film = isFilmwebIdGraphQLUserContent(filmProp) ? filmProp.watchableContent : filmProp;
    if (isMovie(film)) {
      hasPoster = !!film.sanityImagePosterUrl;
      isKinoklubb = film.isKinoklubb;
    } else {
      if (isStreamingContent(film)) {
        hasPoster = !!film.sanityImagePosterUrl || (film.imagesPoster?.length ?? 0) > 0;
      } else {
        if (isWatchableContent(film)) {
          hasPoster = !!film.sanityImagePosterUrl || (film.imagesPosterStreaming?.length ?? 0) > 0;
        } else {
          hasPoster = (film.postersV2?.length ?? 0) > 0 || !!film.mainPosterV2;
          isKinoklubb = !!film.isKinoklubb;
        }
      }
    }
    if (!url) {
      let t10;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = [null, false, false];
        $[3] = t10;
      } else {
        t10 = $[3];
      }
      t8 = t10;
      break bb0;
    }
    let t10;
    if ($[4] !== hasPoster || $[5] !== isKinoklubb || $[6] !== url) {
      t10 = [url, hasPoster, isKinoklubb];
      $[4] = hasPoster;
      $[5] = isKinoklubb;
      $[6] = url;
      $[7] = t10;
    } else {
      t10 = $[7];
    }
    t8 = t10;
  }
  const [_filmUrl, _hasPoster, _isKinoklubb] = t8;
  const film_0 = isFilmwebIdGraphQLUserContent(filmProp) ? filmProp.watchableContent : filmProp;
  if (!_filmUrl) {
    return null;
  }
  const HeadingTag = `h${headerLevel}`;
  let t10;
  let t11;
  let t9;
  if ($[8] !== HeadingTag || $[9] !== _filmUrl || $[10] !== _hasPoster || $[11] !== _isKinoklubb || $[12] !== className || $[13] !== film_0 || $[14] !== height || $[15] !== hideKinoklubbBanner || $[16] !== hideTitle || $[17] !== imageLoading || $[18] !== longTitle || $[19] !== mediumTitle || $[20] !== optionalDisplay || $[21] !== posterMissingClassName || $[22] !== posterMissingText || $[23] !== posterOverlay || $[24] !== showInNaturalFormat || $[25] !== showMovieTitleIfPosterIsMissing || $[26] !== sizes || $[27] !== width) {
    const headingStyles = {
      [cssPosterStyles["lines-3"]]: longTitle,
      [cssPosterStyles["lines-2"]]: mediumTitle
    };
    if ($[31] !== className) {
      t9 = classNames(className, cssPosterStyles.CSSPoster);
      $[31] = className;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    if ($[33] !== _filmUrl || $[34] !== _hasPoster || $[35] !== _isKinoklubb || $[36] !== film_0 || $[37] !== height || $[38] !== hideKinoklubbBanner || $[39] !== imageLoading || $[40] !== posterMissingClassName || $[41] !== posterMissingText || $[42] !== posterOverlay || $[43] !== showInNaturalFormat || $[44] !== showMovieTitleIfPosterIsMissing || $[45] !== sizes || $[46] !== width) {
      t10 = <PosterImage film={film_0} imageLoading={imageLoading} hasPoster={_hasPoster} showMovieTitleIfPosterIsMissing={showMovieTitleIfPosterIsMissing} filmUrl={_filmUrl} isKinoklubb={_isKinoklubb} hideKinoklubbBanner={hideKinoklubbBanner} posterMissingText={posterMissingText} sizes={sizes} width={width} height={height} posterOverlay={posterOverlay} showInNaturalFormat={showInNaturalFormat} posterMissingClassName={posterMissingClassName} />;
      $[33] = _filmUrl;
      $[34] = _hasPoster;
      $[35] = _isKinoklubb;
      $[36] = film_0;
      $[37] = height;
      $[38] = hideKinoklubbBanner;
      $[39] = imageLoading;
      $[40] = posterMissingClassName;
      $[41] = posterMissingText;
      $[42] = posterOverlay;
      $[43] = showInNaturalFormat;
      $[44] = showMovieTitleIfPosterIsMissing;
      $[45] = sizes;
      $[46] = width;
      $[47] = t10;
    } else {
      t10 = $[47];
    }
    t11 = !hideTitle && <HeadingTag className={classNames(cssPosterStyles.H, headingStyles, "poster_title")}><FilmwebLink to={_filmUrl}>{film_0.title}<OptionalPosterInfo film={film_0} optionalDisplay={optionalDisplay} /></FilmwebLink></HeadingTag>;
    $[8] = HeadingTag;
    $[9] = _filmUrl;
    $[10] = _hasPoster;
    $[11] = _isKinoklubb;
    $[12] = className;
    $[13] = film_0;
    $[14] = height;
    $[15] = hideKinoklubbBanner;
    $[16] = hideTitle;
    $[17] = imageLoading;
    $[18] = longTitle;
    $[19] = mediumTitle;
    $[20] = optionalDisplay;
    $[21] = posterMissingClassName;
    $[22] = posterMissingText;
    $[23] = posterOverlay;
    $[24] = showInNaturalFormat;
    $[25] = showMovieTitleIfPosterIsMissing;
    $[26] = sizes;
    $[27] = width;
    $[28] = t10;
    $[29] = t11;
    $[30] = t9;
  } else {
    t10 = $[28];
    t11 = $[29];
    t9 = $[30];
  }
  let t12;
  if ($[48] !== children || $[49] !== t10 || $[50] !== t11 || $[51] !== t9) {
    t12 = <article className={t9}>{t10}{t11}{children}</article>;
    $[48] = children;
    $[49] = t10;
    $[50] = t11;
    $[51] = t9;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  return t12;
}
//#endregion